import React from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import ViewStateManager from '../../utils/viewStateManager';
import { getWorkspacePath } from '../../utils/workspaceUtils';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: { backgroundColor: 'rgba(255,255,255,0.15)' },
  [`& .${linearProgressClasses.bar}`]: { borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.7)' },
}));

class MobileDrawer extends React.Component {
  componentDidUpdate(prevProps) {
    // Close drawer on route change
    if (prevProps.location.pathname !== this.props.location.pathname) {
      if (this.props.isOpen && this.props.onClose) this.props.onClose();
    }
  }

  handleBillingClick = (e) => {
    e.preventDefault();
    const { currentSubscription, currentWorkspace } = this.props;

    if (currentSubscription && currentSubscription.isLocked) {
      if (this.props.onShowAgencyPlans) this.props.onShowAgencyPlans();
    } else {
      ViewStateManager.setBillingTab({ selectedTab: 1 });
      const path = getWorkspacePath('/settings?tab=billing', currentWorkspace?._id);
      this.props.history.push(path);
    }
    if (this.props.onClose) this.props.onClose();
  };

  handleKeyDown = (e) => {
    if (e.key === 'Escape' && this.props.isOpen && this.props.onClose) {
      this.props.onClose();
    }
  };

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  render() {
    const { isOpen, onClose, routes, rtlActive, currentSubscription, currentWorkspace, isCtrlPressed } = this.props;

    const workspacePath = (path) => getWorkspacePath(path, currentWorkspace?._id);
    const activeRoute = (routeName) => this.props.location.pathname.indexOf(routeName) > -1 ? 'active' : '';

    let remainingPercent = 0;
    if (currentSubscription) {
      remainingPercent = Math.min(((currentSubscription.creditsBalance || 0) / (currentSubscription.lastCredited || 1)) * 100, 100);
    }

    return (
      <>
        <div
          className={`mobile-drawer-overlay ${isOpen ? 'open' : ''}`}
          onClick={onClose}
        />
        <div className={`mobile-drawer ${isOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-links">
            {routes.filter(r => !r.workspacePrefixed && !r.hidden).map((route, key) => (
              <li key={key}>
                <NavLink
                  to={workspacePath(route.path)}
                  className={`mobile-nav-link ${activeRoute(route.path)}`}
                  activeClassName="active"
                >
                  <span className="nav-icon">{route.icon}</span>
                  <span>{rtlActive ? route.rtlName : route.name}</span>
                  {isCtrlPressed && route.shortcut && (
                    <span className="sortcut-text">{route.shortcut}</span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          {currentSubscription && (
            <div className="mobile-credit-widget">
              <h3>
                <i className="icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 17.0625C3.21 17.0625 0.9375 14.79 0.9375 12C0.9375 9.72004 2.4675 7.71004 4.665 7.11754C4.9425 7.04254 5.2275 7.18504 5.3325 7.45504C5.79 8.60254 6.765 9.48004 7.9575 9.80254C8.6175 9.98254 9.3825 9.98254 10.0275 9.80254C10.305 9.72754 10.5975 9.87004 10.7025 10.14C10.935 10.7325 11.055 11.3625 11.055 12.0075C11.055 13.4475 10.44 14.82 9.3675 15.7725C8.4525 16.5975 7.2525 17.0625 6 17.0625ZM4.515 8.34754C3.0525 8.94754 2.0625 10.3875 2.0625 12C2.0625 14.1675 3.8325 15.9375 6 15.9375C6.975 15.9375 7.905 15.5775 8.625 14.925C9.465 14.1825 9.9375 13.1175 9.9375 12C9.9375 11.655 9.8925 11.325 9.81 11.0025C9.105 11.115 8.34 11.0775 7.665 10.89C6.3075 10.515 5.1675 9.58504 4.515 8.34754Z" fill="#0E121A" />
                    <path d="M9 11.0625C8.535 11.0625 8.085 11.0025 7.665 10.8825C6.1425 10.4625 4.875 9.3375 4.29 7.86C4.0575 7.275 3.9375 6.645 3.9375 6C3.9375 3.21 6.21 0.9375 9 0.9375C11.79 0.9375 14.0625 3.21 14.0625 6C14.0625 6.645 13.9425 7.275 13.71 7.8675C13.125 9.3375 11.865 10.47 10.335 10.89C9.915 11.0025 9.465 11.0625 9 11.0625ZM9 2.0625C6.8325 2.0625 5.0625 3.8325 5.0625 6C5.0625 6.5025 5.1525 6.99 5.34 7.4475C5.7975 8.595 6.7725 9.4725 7.965 9.795C8.625 9.975 9.39 9.975 10.035 9.795C11.2275 9.4725 12.21 8.5875 12.66 7.4475C12.84 6.99 12.9375 6.5025 12.9375 6C12.9375 3.8325 11.1675 2.0625 9 2.0625Z" fill="#0E121A" />
                    <path d="M12 17.0625C10.7475 17.0625 9.5475 16.5975 8.625 15.765C8.505 15.66 8.4375 15.5025 8.4375 15.345C8.4375 15.1875 8.505 15.03 8.625 14.925C9.4575 14.19 9.9375 13.125 9.9375 12C9.9375 11.4975 9.8475 11.01 9.66 10.5525C9.6 10.4025 9.6075 10.2375 9.6825 10.095C9.7575 9.95248 9.885 9.84748 10.035 9.80248C11.2275 9.47998 12.2025 8.60248 12.66 7.45498C12.765 7.18498 13.0575 7.04998 13.3275 7.11748C15.525 7.71748 17.055 9.71998 17.055 12C17.0625 14.79 14.79 17.0625 12 17.0625ZM9.84 15.2925C10.4775 15.7125 11.22 15.9375 12 15.9375C14.1675 15.9375 15.9375 14.1675 15.9375 12C15.9375 10.3875 14.9475 8.94748 13.485 8.34748C12.93 9.40498 12 10.245 10.89 10.695C11.0025 11.115 11.0625 11.5575 11.0625 12C11.0625 13.215 10.6275 14.385 9.84 15.2925Z" fill="#0E121A" />
                  </svg>
                </i>
                <span>{currentSubscription.status === 'free' ? 'Free Credits' : 'Credits'}</span>
              </h3>
              <div className="progress-area-box">
                <h4>Remaining: <span>{parseFloat(currentSubscription.creditsBalance || 0).toFixed(0)}</span></h4>
                <div className="progress-bar-area">
                  <BorderLinearProgress variant="determinate" value={remainingPercent} />
                </div>
              </div>
              <a
                href={workspacePath('/settings?tab=billing')}
                onClick={this.handleBillingClick}
                className="btn-style-new"
              >
                {currentSubscription.isLocked ? 'Pay Now' : 'Add Credits'}
              </a>
            </div>
          )}
        </div>
      </>
    );
  }
}

const mapStateToProps = ({ Settings: { currentSubscription, currentWorkspace } }) => ({
  currentSubscription,
  currentWorkspace,
});

export default withRouter(connect(mapStateToProps)(MobileDrawer));
